import { useState, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../../components/Auth/AuthProvider';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  addUsersToFeatureFlag,
  removeUsersFromFeatureFlag,
} from '../services/featureFlagApi';

interface UserManagementProps {
  flagId: string;
  userIds: string[];
  onError: (error: string) => void;
}

export function UserManagement({
  flagId,
  userIds,
  onError,
}: Readonly<UserManagementProps>) {
  const { actions, authInfo } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [newIdentifierInput, setNewIdentifierInput] = useState('');

  // Get organization from authInfo if it exists
  const userOrganization = authInfo?.organization;

  const addIdentifierMutation = useMutation({
    mutationFn: async (identifier: string) => {
      const token = await actions?.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      return addUsersToFeatureFlag(flagId, [identifier], token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
      setNewIdentifierInput('');
    },
    onError: (error) => onError(error.message),
  });

  const removeIdentifierMutation = useMutation({
    mutationFn: async (identifier: string) => {
      const token = await actions?.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      return removeUsersFromFeatureFlag(flagId, [identifier], token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
    },
    onError: (error) => onError(error.message),
  });

  const handleNewIdentifierInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' && newIdentifierInput.trim()) {
      e.preventDefault();
      handleAddIdentifier();
    }
  };

  const handleAddIdentifier = () => {
    if (!newIdentifierInput.trim()) {
      return;
    }

    if (!userIds.includes(newIdentifierInput.trim())) {
      addIdentifierMutation.mutate(newIdentifierInput.trim());
    } else {
      onError('Identifier already exists in the feature flag');
    }
  };

  const handleAddOrganization = () => {
    if (!userOrganization) {
      return;
    }

    if (!userIds.includes(userOrganization)) {
      addIdentifierMutation.mutate(userOrganization);
    } else {
      onError('Organization already exists in the feature flag');
    }
  };

  const handleRemoveIdentifier = (id: string) => {
    if (confirm(`Remove ${id} from this feature flag?`)) {
      removeIdentifierMutation.mutate(id);
    }
  };

  const isOrganizationId = (id: string) => {
    return !id.includes('auth0|') && !id.includes('@');
  };

  return (
    <Box sx={{ my: 2, px: 2 }}>
      <Typography variant='subtitle1' gutterBottom>
        Users & Organizations
      </Typography>

      <Box sx={{ mb: 3 }}>
        {userIds.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {userIds.map((id) => (
              <Chip
                key={id}
                label={id}
                color={isOrganizationId(id) ? 'secondary' : 'primary'}
                onDelete={() => handleRemoveIdentifier(id)}
                disabled={removeIdentifierMutation.isPending}
              />
            ))}
          </Box>
        ) : (
          <Typography color='text.secondary'>
            No users or organizations have this feature flag
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mt: 3 }}>
        <Typography variant='subtitle2' gutterBottom>
          Add User or Organization
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems='flex-start'
        >
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <TextField
              fullWidth
              label='User ID or Organization'
              value={newIdentifierInput}
              onChange={(e) => setNewIdentifierInput(e.target.value)}
              onKeyDown={handleNewIdentifierInputKeyDown}
              disabled={addIdentifierMutation.isPending}
              helperText='Press Enter to add identifier'
              size='small'
            />
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant='contained'
              onClick={handleAddIdentifier}
              disabled={
                !newIdentifierInput.trim() || addIdentifierMutation.isPending
              }
              fullWidth
              size='medium'
            >
              {addIdentifierMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                'Add Identifier'
              )}
            </Button>
          </Box>
        </Stack>
      </Box>

      {userOrganization && (
        <Box sx={{ mt: 3 }}>
          <Typography variant='subtitle2' gutterBottom>
            Add Current Organization
          </Typography>

          <Alert severity='info' sx={{ mb: 2 }}>
            Your current organization: <strong>{userOrganization}</strong>
          </Alert>

          <Button
            variant='outlined'
            onClick={handleAddOrganization}
            disabled={
              userIds.includes(userOrganization) ||
              addIdentifierMutation.isPending
            }
            fullWidth
          >
            {addIdentifierMutation.isPending ? (
              <CircularProgress size={24} />
            ) : (
              `Add Organization: ${userOrganization}`
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
}
